import React, { useState, useEffect, useRef } from "react";
import { Calendar } from "primereact/calendar";
import { SelectButton } from "primereact/selectbutton";
import "primereact/resources/primereact.min.css";
import "primereact/resources/themes/lara-light-indigo/theme.css";

interface DateFilterProps {
  onDateRangeChange: (startDate: string, endDate: string) => void;
}

// View type options - simplified to only month and financial year
type ViewType = "month" | "financialYear";

const DateFilter: React.FC<DateFilterProps> = ({ onDateRangeChange }) => {
  const [viewType, setViewType] = useState<ViewType>("month");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [displayText, setDisplayText] = useState("");
  const [showCalendar, setShowCalendar] = useState(false);
  const calendarRef = useRef<any>(null);

  const viewOptions = [
    { label: "Month", value: "month" },
    { label: "Financial Year", value: "financialYear" },
  ];

  // Format date as DD/MM/YYYY
  const formatDate = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Custom class for styling SelectButton
  useEffect(() => {
    // Add custom styles for PrimeReact SelectButton
    const styleElement = document.createElement("style");
    styleElement.innerHTML = `
      .custom-select-button .p-selectbutton {
        border-radius: 0.375rem 0 0 0.375rem;
        overflow: hidden;
        height: 44px;
        display: flex;
      }
      .custom-select-button .p-selectbutton .p-button {
        padding: 0.625rem 1.5rem;
        font-weight: 500;
        font-size: 0.875rem;
        line-height: 1.25rem;
        border: 1px solid #e5e7eb;
        background: white;
        color: #374151;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .custom-select-button .p-selectbutton .p-button:not(:last-child) {
        border-right: 1px solid #e5e7eb;
      }
      
      .custom-select-button .p-selectbutton .p-button:focus {
        box-shadow: none;
        outline: none;
      }
    `;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []); // SelectButton options

  // Get month name
  const getMonthName = (month: number): string => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return months[month];
  };

  // Update the display text based on current date and view type
  const updateDisplayText = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    if (viewType === "month") {
      setDisplayText(`${getMonthName(month)} ${year}`);
    } else {
      // Financial year (April to March)
      const fiscalYear = month >= 3 ? year : year - 1;
      setDisplayText(`April ${fiscalYear} - March ${fiscalYear + 1}`);
    }
  };

  // Generate date range based on current date and view type
  const generateDateRange = () => {
    let startDate: Date;
    let endDate: Date;

    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    if (viewType === "month") {
      // Month view: first day to last day of month
      startDate = new Date(year, month, 1);
      endDate = new Date(year, month + 1, 0); // Last day of month
    } else {
      // Financial year (April to March)
      const fiscalYear = month >= 3 ? year : year - 1;
      startDate = new Date(fiscalYear, 3, 1); // April 1st
      endDate = new Date(fiscalYear + 1, 2, 31); // March 31st
    }

    onDateRangeChange(formatDate(startDate), formatDate(endDate));
  };

  // Handle previous/next navigation
  const handlePrevious = () => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);

      if (viewType === "month") {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        // Financial Year navigation - move back a year
        newDate.setFullYear(newDate.getFullYear() - 1);
      }

      return newDate;
    });
  };

  const handleNext = () => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);

      if (viewType === "month") {
        newDate.setMonth(newDate.getMonth() + 1);
      } else {
        // Financial Year navigation - move forward a year
        newDate.setFullYear(newDate.getFullYear() + 1);
      }

      return newDate;
    });
  };

  // Handle date change from calendar
  const handleDateChange = (date: Date | null) => {
    if (date) {
      setCurrentDate(date);
      setShowCalendar(false);
    }
  };

  // Handle view type change
  const handleViewTypeChange = (value: ViewType) => {
    if (value) {
      setViewType(value);
      setShowCalendar(false);
    }
  };

  // Update display text and date range when currentDate or viewType changes
  useEffect(() => {
    updateDisplayText();
    generateDateRange();
  }, [currentDate, viewType]);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target as Node)
      ) {
        setShowCalendar(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="relative" ref={calendarRef}>
      <div className="flex gap-3 flex-wrap">
        {/* SelectButton for view type */}
        <div className="custom-select-button">
          <SelectButton
            value={viewType}
            options={viewOptions}
            onChange={(e) => handleViewTypeChange(e.value)}
            className="shadow-sm"
          />
        </div>

        {/* Custom calendar display with navigation */}
        <div className="flex items-center border border-gray-200 rounded-sm bg-white overflow-hidden shadow-sm h-11">
          {/* Left arrow */}
          <button
            className="h-11 rounded-l-sm px-2 bg-gold text-white hover:text-gray-800 border border-gold hover:border-gold hover:bg-gray-50 flex items-center justify-center transition-colors mb-0 outline-none"
            onClick={handlePrevious}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>

          {/* Date display - clickable to show calendar */}
          <div
            className="csCalendar relative h-11 px-5 flex items-center justify-center text-gray-800 font-medium cursor-pointer transition-colors flex-1 border border-solid border-gray-300 border-x-0"
            onClick={() => setShowCalendar(!showCalendar)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="mr-2 text-gold"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            {displayText}
          </div>

          {/* Right arrow */}
          <button
            className="h-11 px-2 rounded-r-sm bg-gold text-white hover:text-gray-800 border border-gold hover:border-gold hover:bg-gray-50 flex items-center justify-center transition-colors mb-0 outline-none"
            onClick={handleNext}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        </div>
      </div>

      {/* PrimeReact Calendar popup */}
      {showCalendar && (
        <div className="csCalendarPop absolute right-0 top-12 z-10 bg-white border border-gray-200 rounded-md shadow-lg p-2">
          <Calendar
            value={currentDate}
            onChange={(e) => handleDateChange(e.value as Date)}
            inline
            monthNavigator
            yearNavigator
            yearRange={`${new Date().getFullYear() - 10}:${
              new Date().getFullYear() + 10
            }`}
            view={viewType === "month" ? "month" : "year"}
            className="border-0 calendar_custom"
            panelClassName="border-0 shadow-none"
          />
        </div>
      )}
    </div>
  );
};

export default DateFilter;
